import {
  Card,
  ECardBorderRadiusSize,
  ECardMediaSize,
  EHeadingStyleVariant,
  ETextStyleVariant,
  Heading,
  Text,
} from '@outdoorsyco/bonfire';
import NextLink from 'next/link';
import React from 'react';
import { useIntl } from 'react-intl';

import { IActivity, IAddress } from '@/components/route/campground/campgroundTypes';
import MapStatic from '@/components/ui/MapStatic/MapStatic';
import { getCampgroundNearbyParkGuides } from '@/constants/parks';
import { ICoordinates } from '@/utility/commonDataTypes';

import { ClaimedCampgroundHighlights } from './ClaimedCampgroundHighlights';
import { Highlights } from './Highlights';
import { Location } from './Location';

type TMapSize = {
  width: number;
  height: number;
};

const mapSizes: TMapSize[] = [
  { width: 576, height: 360 },
  { width: 700, height: 450 },
  { width: 920, height: 636 },
  { width: 1260, height: 720 },
];

type TNearbyParksProps = {
  name?: string;
  location: ICoordinates;
  activities?: IActivity[];
  address?: IAddress;
  isClaimed?: boolean;
};

export const NearbyParks = ({
  name,
  location,
  activities,
  address,
  isClaimed,
}: TNearbyParksProps) => {
  const intl = useIntl();

  return (
    <div>
      <Heading variant={EHeadingStyleVariant.H5}>
        {intl.formatMessage({ defaultMessage: 'Discover the best of what’s around', id: 'WKcvW6' })}
      </Heading>

      <div className="items-center mt-12 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <div className="relative md:col-span-8">
          <MapStatic
            className="w-full overflow-hidden border border-gray-200 rounded-2xl"
            viewport={{ lat: location.lat, lon: location.lon, zoom: 10 }}
            overlay={[
              {
                lat: location.lat,
                lon: location.lon,
                url: 'https://d1o5877uy6tsnd.cloudfront.net/img/location-map-pins/campground.png',
              },
            ]}
            sourceSizes={mapSizes}
          />
        </div>

        <div className="flex flex-col gap-12 md:col-span-4">
          {activities && <Highlights activities={activities} />}
          {isClaimed && <ClaimedCampgroundHighlights />}
          <Location location={location} address={address} />
        </div>
      </div>

      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {getCampgroundNearbyParkGuides(name)
          .slice(0, 3)
          .map(guide => (
            <NextLink key={guide.link} href={guide.link} passHref legacyBehavior>
              <Card borderRadiusSize={ECardBorderRadiusSize.Large} withBoxShadow>
                <Card.Media image={guide.image} size={ECardMediaSize.Medium} />
                <Card.Content className="p-6">
                  <Text variant={ETextStyleVariant.LargeBold}>{guide.name}</Text>
                </Card.Content>
              </Card>
            </NextLink>
          ))}
      </div>
    </div>
  );
};
